// Admin ban / unban, driven from /admin/moderation.
//
// The work happens in the setUserBanned callable: disabling a Firebase Auth user
// and sweeping their prompts both need the Admin SDK, and the sweep in
// particular MUST be server-side - a client loop over updateDoc would go through
// promptValidation(), so legacy prompts that fail its title/gender checks would
// silently be skipped and stay visible.
//
// The ban state lives in a `bans` collection the client can only read, never
// write (see firestore.rules). That is the whole answer to "can a banned user
// unban themselves": there is nowhere for them to write.
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, firestore, functions } from '../firebase';
import { GET_BANNED_USERS, SET_USER_BANNED } from './types';
import { openSnackbar } from './snackbarActions';

const callSetUserBanned = httpsCallable(functions, 'setUserBanned');

export const getBannedUsers = () => async dispatch => {
  try {
    const snapshots = await getDocs(collection(firestore, 'bans'));
    const bannedUsers = {};
    snapshots.docs.forEach(docSnap => {
      bannedUsers[docSnap.id] = { ...docSnap.data(), uid: docSnap.id };
    });
    dispatch({ type: GET_BANNED_USERS, payload: { bannedUsers } });
  } catch (error) {
    console.error(error);
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not load the list of banned users.'
      })
    );
  }
};

// A custom claim only enters an ID token when that token is minted. An admin
// whose claim was granted after they signed in carries a token without it until
// the next hourly refresh - so the server rejects them while the UI, which
// gates on username, still shows every admin control. Forcing a refresh before
// the call costs one round trip and removes the whole class of confusion.
const ensureAdminClaim = async () => {
  const user = auth.currentUser;
  if (!user) return { ok: false, reason: 'not signed in' };
  let token = await user.getIdTokenResult();
  if (token.claims && token.claims.admin === true) return { ok: true };
  token = await user.getIdTokenResult(true);
  if (token.claims && token.claims.admin === true) return { ok: true };
  return { ok: false, reason: 'no admin claim' };
};

export const setUserBanned = (uid, banned, options = {}) => async dispatch => {
  const { username, reason } = options;
  try {
    const claim = await ensureAdminClaim();
    if (!claim.ok) {
      dispatch(
        openSnackbar('ErrorSnackbar', {
          message:
            claim.reason === 'not signed in'
              ? 'You are signed out. Sign in again and retry.'
              : 'This account can see the admin pages but does not hold the admin claim the server requires. Sign out and back in; if it persists, the claim needs granting with scripts/setAdminClaim.js.',
          autoHideDuration: 12000
        })
      );
      return;
    }
    const response = await callSetUserBanned({ uid, banned, reason });
    const result = (response && response.data) || {};

    // patched into the slice rather than refetched: several reports can share
    // one author, and a refetch of the queue would throw the admin back to the
    // top of the list they are working through
    dispatch({
      type: SET_USER_BANNED,
      payload: { uid, ban: { uid, username, reason, active: banned } }
    });

    let message;
    if (banned) {
      // Report against the TOTAL they own, not just the number changed. "Hid 18
      // prompts" invites the question "out of how many?", and the answer decides
      // whether the sweep did its job.
      const hid = result.promptsHiddenByBan || 0;
      const total = result.promptsTotal;
      const already = result.promptsAlreadyHidden || 0;
      message = `Banned ${username || uid}. Hid ${hid}${
        typeof total === 'number' ? ` of their ${total}` : ''
      } prompt(s).`;
      // otherwise the two numbers disagree and it reads like a partial failure
      if (already > 0) {
        message += ` ${already} were already hidden and were left as they were.`;
      }
      // re-running a ban is a resumable success, not an error - the usual reason
      // an admin clicks twice is that the first attempt died mid-sweep
      if (result.alreadyBanned) {
        message = `Already banned — re-ran the sweep and hid ${hid} more prompt(s).`;
      }
      if (result.authUserMissing) {
        message += ' Their sign-in account no longer exists.';
      }
    } else {
      message = `Unbanned ${username || uid}. Restored ${result.promptsRestored || 0} prompt(s).`;
      if (result.wasNotBanned) {
        message = `${username || uid} was not banned. Restored ${result.promptsRestored || 0} prompt(s) anyway.`;
      }
    }
    dispatch(openSnackbar('SuccessSnackbar', { message, autoHideDuration: 8000 }));
  } catch (error) {
    console.error('setUserBanned failed', { code: error && error.code, message: error && error.message, details: error && error.details });
    // the callable's own messages are the useful part here ("Refusing to ban an
    // admin", "That is one of our own accounts"), so surface them rather than a
    // generic failure
    const code = (error && error.code) || '';
    let message = (error && error.message) || 'Could not change the ban.';
    // The SDK reports EVERY failure that never reached the function as a bare
    // "internal" - blocked request, DNS, offline, extension. Saying so is the
    // difference between a user retrying forever and a user checking a blocker.
    if (code === 'functions/internal' || message === 'internal') {
      message =
        'The request never reached the server. Something between the browser and cloudfunctions.net blocked it - an ad/privacy blocker or VPN is the usual cause. Check the Network tab for a failed setUserBanned request.';
    } else if (code === 'functions/unauthenticated') {
      message = 'Your session expired. Sign in again and retry.';
    }
    dispatch(
      openSnackbar('ErrorSnackbar', { message, autoHideDuration: 14000 })
    );
  }
};
